import { useEffect, useMemo, useState } from 'react';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import type { SizeGuide2 as SizeGuideSchema } from '@amplience/content-types/typings/c-size-guide';
import type { RequiredFlexibleText } from '@amplience/content-types/typings/p-partials';
import type { PDPLayoutSlot as PDPLayoutSchema } from '@amplience/content-types/typings/s-pdp-layout';
import type { CentPrecisionMoney } from '@storefront/lib-global';
import { PromotionLayoutsProvider, useDidMount } from '@storefront/lib-global';
import type {
  CarouselProduct,
  Product,
  ProductMaterial,
  ProductReference,
  StyleProduct,
  Variant,
} from '@storefront/lib-global/codegen/types';
import { useStaticContext } from '@storefront/lib-global/context/staticContext';
import { FinalSale } from '@storefront/lib-global/globalComponents/finalSale/FinalSale';
import type { HandlebarTemplateProps } from '@storefront/lib-global/globalComponents/handlebarTemplate';
import { Reviews, ReviewsMini, TrustpilotScript } from '@storefront/lib-global/globalComponents/trustpilot';
import { useKlarnaWidgetPriceMutationObserver } from '@storefront/lib-global/hooks/useKlarnaWidgetPriceMutationObserver';
import { useMediaMatch } from '@storefront/lib-global/hooks/useMediaMatch';
import { media } from '@storefront/lib-global/stylings';
import { sendViewItemEvent } from '@storefront/lib-global/utils/gtm/events/view_item/sendViewItemEvent';
import { getItem, removeItem } from '@storefront/lib-global/utils/storage';
import { SizeAndWidthContextProvider, useSizeAndWidthContext } from '../../../context';
import { useProductPrice } from '../../../hooks';
import { renderBelowAddToBagContent } from '../BelowAddToBagContent';
import BelowTheFoldSection from '../BelowTheFoldSection';
import { ProductCustomShippingAndReturns } from '../ProductCustomShippingAndReturns/ProductCustomShippingAndReturns';
import type { ProductFeatureTypes } from '../ProductFeature';
import ProductFeatureList from '../ProductFeatureList';
import ProductName from '../ProductName/ProductName';
import { SizeAndWidthSelectors } from '../SizeAndWidthSelectors/SizeAndWidthSelectors';
import ColourSelector from '../colourSelector/ColourSelector';
import { KlarnaPdpWidget, KlarnaPdpWidgetScript } from '../deliveryAndKlarna';
import { FreeShippingAndReturns } from '../freeShippingAndReturns/FreeShippingAndReturns';
import MaterialSection from '../materialsAndCare/MaterialSection';
import ProductBreadcrumb from '../productBreadcrumb/ProductBreadcrumb';
import ProductDescription from '../productDescription/ProductDescription';
import { ProductDetailMedia, ProductDynamicMedia } from '../productMedia/ProductMedia';
import ProductPrice from '../productPrice/ProductPrice';
import {
  DeliveryAndKlarnaContainer,
  DesktopFinalSaleContainer,
  DesktopProductPriceContainer,
  MobileFinalSaleContainer,
  MobileProductNameContainer,
  MobileProductPriceContainer,
  PDPAmpComponentsAndDescription,
  ProductInfoContainer,
  ProductInfoStickyContainer,
  ProductPageContainer,
  ProductPageContent,
  ProductWrapper,
} from './ProductDetail.styles';

const removeDuplicateReducer = (type: 'size' | 'fitLabel') => (acc: string[], variant: Variant) => {
  const value = variant[type] as string;
  if (value) {
    return !acc.includes(value) ? [...acc, value] : acc;
  }
  return acc;
};
const buildUniqueSizes = (variantsInput: Variant[]): string[] =>
  [...variantsInput]
    .sort((a, b) => +(a.globalSize as string) - +(b.globalSize as string))
    .reduce(removeDuplicateReducer('size'), []);

const buildUniqueWidths = (variantsInput: Variant[]): string[] =>
  [...variantsInput]
    .sort((a, b) => (a.globalFit as string)?.localeCompare(b.globalFit as string))
    .reduce(removeDuplicateReducer('fitLabel'), []);

export type ProductDetailProps = {
  pdpLayout: PDPLayoutSchema;
  product: Product & {
    productTechnologiesContentData?: ProductFeatureTypes[];
  };
  carouselData: { deliveryId: string; carouselProducts: CarouselProduct[] }[];
  handlebarComponents: HandlebarTemplateProps[] | null;
};

const DynamicProductZoom = dynamic(
  async () => {
    const { ProductZoom } = await import('../productZoom/ProductZoom');
    return ProductZoom;
  },
  { ssr: false },
);

export function ProductDetail({ product, pdpLayout, carouselData, handlebarComponents }: ProductDetailProps) {
  const {
    variants,
    productTechnologiesContentData,
    description,
    features,
    imageUrls,
    videoUrl,
    colors,
    slug,
    key,
    name,
    styleName = '',
    mediumColor = '',
    references,
    materials,
    commercetoolsType,
    gender,
    categoriesPath,
    brand,
    primaryCategory,
    badgesV2,
    subType,
    shippingAndReturns,
    finalSale,
    priceRanges,
    promotion,
    ratings,
  } = {
    ...product,
  };
  const { slotContent = null, hasReviews: isTrustpilotEnabled = true, promotionLayouts } = { ...pdpLayout };
  const isDesktop = useMediaMatch(media.greaterThan('lg'));
  const didMount = useDidMount();
  const { locale } = useRouter();
  const { baseUrl } = useStaticContext();

  const [isZoomActive, setIsZoomActive] = useState(false);
  const [activeMediaSlide, setActiveSlide] = useState(0);
  const [productColor, setProductColor] = useState('');
  const [initialSize, setInitialSize] = useState('');
  const [initialWidth, setInitialWidth] = useState('');

  const sizeGuideTitleValue = slotContent?.sizeGuide
    ? ((slotContent?.sizeGuide as unknown as SizeGuideSchema).title as RequiredFlexibleText)
    : {};

  const uniqueSizes = useMemo(() => buildUniqueSizes(variants), [variants]);
  const uniqueWidths = useMemo(() => buildUniqueWidths(variants), [variants]);
  const { selectedSize, selectedWidth } = useSizeAndWidthContext();
  const { actualPriceCentPrecisionMoney, thenPriceCentPrecisionMoney, wasPriceCentPrecisionMoney } = useProductPrice({
    selectedSize,
    selectedWidth,
    variants,
  });

  const shouldShowReviews = isTrustpilotEnabled && (ratings?.total ?? 0) > 0;
  const productId = colors && colors.length > 0 ? colors.map((color) => color.key).join(',') : key;

  const productMediaUrls = useMemo(() => (videoUrl ? [...imageUrls, videoUrl] : imageUrls), [imageUrls, videoUrl]);

  const reviewsMiniWidget = useMemo(() => {
    if (!shouldShowReviews) return null;
    return <ReviewsMini productId={productId} />;
  }, [shouldShowReviews, productId]);

  const isPaginationDynamic = productMediaUrls.length > 4;
  const measureAtHome = slotContent?.sizeGuide
    ? (slotContent?.sizeGuide as unknown as SizeGuideSchema).measureAtHome
    : null;
  const bookingForm = slotContent?.sizeGuide
    ? (slotContent?.sizeGuide as unknown as SizeGuideSchema).bookingForm
    : null;
  const copyBlock = slotContent?.sizeGuide ? (slotContent.sizeGuide as unknown as SizeGuideSchema).copyBlock : null;

  const productName = `${styleName || ''} ${mediumColor || ''}`;
  const shortProductName = styleName || '';

  const { itemListId, itemListName } = getItem('listingPage', true, 'sessionStorage') || {};

  const { dataPurchaseAmount } = useKlarnaWidgetPriceMutationObserver(actualPriceCentPrecisionMoney?.centAmount);

  const setActiveMediaSlide = (value: number) => {
    setActiveSlide(value);
  };

  const isCustomShippingAndReturnsVisible = Boolean(
    !finalSale?.isFinalSale && !!shippingAndReturns?.image && shippingAndReturns?.text,
  );

  const productData = {
    variants,
    actualPrice: actualPriceCentPrecisionMoney as Partial<CentPrecisionMoney>,
    productName,
    key,
    brand,
    primaryCategory,
    colors,
    itemListName,
    itemListId,
    wasPrice: wasPriceCentPrecisionMoney as Partial<CentPrecisionMoney>,
    thenPrice: thenPriceCentPrecisionMoney as Partial<CentPrecisionMoney>,
    images: imageUrls,
    slug: slug as string,
  };

  useEffect(() => {
    sendViewItemEvent(productData, baseUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productName]);

  useEffect(() => {
    const { pdpSelectedSize, pdpSelectedWidth } = getItem('pdpStockAvailabilityData', true, 'sessionStorage') || {};
    const isPdpBackToResults = getItem('isPdpBackToResults', false, 'sessionStorage');

    if (isPdpBackToResults && pdpSelectedSize && pdpSelectedWidth) {
      setInitialSize(pdpSelectedSize);
      setInitialWidth(pdpSelectedWidth);
    }

    if (!isPdpBackToResults) {
      removeItem('pdpStockAvailabilityData', 'sessionStorage');
      removeItem('pdpStockAvailabilityQuery', 'sessionStorage');
      removeItem('pdpStockAvailabilityActiveTab', 'sessionStorage');
    }

    removeItem('isPdpBackToResults', 'sessionStorage');
  }, []);

  return (
    <PromotionLayoutsProvider promotionLayouts={promotionLayouts}>
      <section>
        <KlarnaPdpWidgetScript id="klarna-pdp-widget-script" />
        {shouldShowReviews && <TrustpilotScript />}
        {categoriesPath && categoriesPath.length > 0 && <ProductBreadcrumb categoriesPath={categoriesPath} />}
        <ProductPageContainer id="pdp-page-container">
          {isZoomActive ? (
            <DynamicProductZoom
              productMediaUrls={productMediaUrls}
              isZoomActive={isZoomActive}
              setIsZoomActive={setIsZoomActive}
              activeMediaSlide={activeMediaSlide}
              setActiveMediaSlide={setActiveMediaSlide}
              productName={productName ?? ''}
              productKey={key}
            />
          ) : null}
          <ProductPageContent>
            {didMount && !isDesktop && (
              <MobileProductNameContainer data-testid="mobileTitleContainer">
                <ProductName name={shortProductName} colorName={mediumColor} />
              </MobileProductNameContainer>
            )}

            <SizeAndWidthContextProvider
              priceRanges={priceRanges}
              variants={variants}
              uniqueSizes={uniqueSizes}
              uniqueWidths={uniqueWidths}
              initialSize={initialSize}
              initialWidth={initialWidth}
            >
              {!isDesktop && (
                <MobileProductPriceContainer data-testid="mobilePriceContainer">
                  <ProductPrice promotion={promotion} priceRanges={priceRanges} tooltipContent={slotContent?.tooltip} />
                  {finalSale?.text && (
                    <MobileFinalSaleContainer>
                      <FinalSale
                        text={finalSale.text}
                        textColor={finalSale.textColor}
                        backgroundColor={finalSale.backgroundColor}
                      />
                    </MobileFinalSaleContainer>
                  )}
                  {reviewsMiniWidget}
                </MobileProductPriceContainer>
              )}
              <ProductWrapper
                $isZoomOverlayActive={isZoomActive}
                data-testid="productContainerDesktop"
                id="pdp-container-desktop"
              >
                {isPaginationDynamic ? (
                  <ProductDynamicMedia
                    productMediaUrls={productMediaUrls}
                    setIsZoomActive={setIsZoomActive}
                    activeMediaSlide={activeMediaSlide}
                    setActiveMediaSlide={setActiveMediaSlide}
                    isPaginationDynamic={isPaginationDynamic}
                    productKey={key}
                    badgesV2={badgesV2}
                    productName={productName}
                    subType={subType || undefined}
                  />
                ) : (
                  <ProductDetailMedia
                    productMediaUrls={productMediaUrls}
                    setIsZoomActive={setIsZoomActive}
                    activeMediaSlide={activeMediaSlide}
                    setActiveMediaSlide={setActiveMediaSlide}
                    badgesV2={badgesV2}
                    productKey={key}
                    productName={productName}
                    subType={subType || undefined}
                  />
                )}
                <ProductInfoContainer>
                  <ProductInfoStickyContainer id="pdp-sticky-container">
                    {didMount && isDesktop && <ProductName name={shortProductName} colorName={mediumColor} />}

                    {isDesktop && (
                      <DesktopProductPriceContainer>
                        <ProductPrice promotion={promotion} tooltipContent={slotContent?.tooltip} />
                        {finalSale?.text && (
                          <DesktopFinalSaleContainer>
                            <FinalSale
                              text={finalSale.text}
                              backgroundColor={finalSale.backgroundColor}
                              textColor={finalSale.textColor}
                            />
                          </DesktopFinalSaleContainer>
                        )}
                        {reviewsMiniWidget}
                      </DesktopProductPriceContainer>
                    )}
                    {colors && colors?.length !== 0 ? (
                      <ColourSelector
                        colors={colors as StyleProduct[]}
                        productKey={key}
                        slug={slug as string}
                        setProductColor={setProductColor}
                        name={styleName || name || ''}
                      />
                    ) : null}
                    <SizeAndWidthSelectors
                      sizeGuideTitle={sizeGuideTitleValue}
                      stockLevel={slotContent?.stockLevel}
                      references={references as ProductReference[]}
                      gender={gender as string[]}
                      type={commercetoolsType}
                      productImageURL={imageUrls[0]}
                      productName={productName}
                      productColor={productColor}
                      measureAtHome={measureAtHome}
                      bookingForm={bookingForm}
                      copyBlock={copyBlock}
                      productKey={product.key}
                    />
                    <PDPAmpComponentsAndDescription>
                      <DeliveryAndKlarnaContainer>
                        <KlarnaPdpWidget dataPurchaseAmount={dataPurchaseAmount} />
                      </DeliveryAndKlarnaContainer>
                      <DeliveryAndKlarnaContainer>
                        {slotContent?.belowATBContent &&
                          renderBelowAddToBagContent({
                            belowATBContent: slotContent.belowATBContent,
                            handlebarComponents,
                            locale,
                          })}
                      </DeliveryAndKlarnaContainer>
                      <DeliveryAndKlarnaContainer
                        $isCustomShippingAndReturnsVisible={isCustomShippingAndReturnsVisible}
                      >
                        <ProductCustomShippingAndReturns
                          isVisible={isCustomShippingAndReturnsVisible}
                          text={shippingAndReturns?.text}
                          image={shippingAndReturns?.image}
                        />
                        <ProductDescription description={description} features={features} />
                      </DeliveryAndKlarnaContainer>
                    </PDPAmpComponentsAndDescription>
                    <div>
                      <FreeShippingAndReturns variants={variants} />
                      {slotContent?.materialsAndCareBlock ? (
                        <MaterialSection
                          materialsAndCareBlock={slotContent.materialsAndCareBlock}
                          materials={materials as ProductMaterial[]}
                          type={commercetoolsType}
                        />
                      ) : null}
                    </div>
                    {productTechnologiesContentData?.length !== 0 ? (
                      <ProductFeatureList technologies={productTechnologiesContentData} />
                    ) : null}
                  </ProductInfoStickyContainer>
                </ProductInfoContainer>
              </ProductWrapper>
            </SizeAndWidthContextProvider>
          </ProductPageContent>
        </ProductPageContainer>
        {shouldShowReviews && (
          <Reviews productId={productId} productName={productName} numberOfReviews={ratings?.total ?? 0} />
        )}
        <BelowTheFoldSection
          belowTheFoldContent={slotContent?.belowTheFoldContent}
          carouselData={carouselData}
          singleProductKey={key}
          handlebarComponents={handlebarComponents}
          isZoomActive={isZoomActive}
        />
      </section>
    </PromotionLayoutsProvider>
  );
}

export default ProductDetail;
